import {
  Box,
  Flex,
  ListItem,
  Stack,
  Text,
  UnorderedList,
} from '@chakra-ui/react';
import type {
  VoucherGetByCodeResponse,
  VoucherRewardMessage,
  VoucherTermMessage,
  VoucherWarningMessage,
} from '@diamond/shared/types';
import { WarningAmberOutlined } from '@mui/icons-material';
import Image from 'next/image';

interface VoucherCardDetailProps {
  voucher: VoucherGetByCodeResponse;
}

const getVoucherDescription = (voucher: VoucherGetByCodeResponse): string => {
  return voucher.desc || '';
};

const getRewardMessages = (
  voucher: VoucherGetByCodeResponse
): VoucherRewardMessage[] => {
  return voucher.reward_messages || [];
};

const getTermMessages = (
  voucher: VoucherGetByCodeResponse
): VoucherTermMessage[] => {
  return voucher.term_messages || [];
};

const getWarningMessages = (
  voucher: VoucherGetByCodeResponse
): VoucherWarningMessage[] => {
  return voucher.warning_messages || [];
};

const VoucherCardDetail = ({ voucher }: VoucherCardDetailProps) => {
  const bannerUrl = voucher.banner_url || '/images/voucher.png';
  const description = getVoucherDescription(voucher);
  const rewardMessages = getRewardMessages(voucher);
  const termMessages = getTermMessages(voucher);
  const warningMessages = getWarningMessages(voucher);

  return (
    <Box>
      {/* Banner Image */}
      <Box
        position="relative"
        w="100%"
        h="134px"
        overflow="hidden"
        mb="20px"
        borderRadius="8px"
      >
        <Image
          src={bannerUrl}
          width={488}
          height={134}
          alt={voucher.title}
          style={{
            borderRadius: 8,
            objectFit: 'cover',
            width: '100%',
            height: '100%',
          }}
        />
      </Box>

      <Flex flexDirection="column" gap="3" p="4px">
        {/* Title and Description */}
        <Box>
          <Text fontSize="16px" fontWeight="bold" color="#000000" mb="1">
            {voucher.title}
          </Text>
          {description && (
            <Text fontSize="12px" color="#8E8E93" fontWeight="medium">
              {description}
            </Text>
          )}
        </Box>

        {/* Reward Messages Section */}
        {rewardMessages.length > 0 && (
          <Flex flexDirection="column">
            {rewardMessages.map((reward, idx) => (
              <Box key={idx} bg="#F2F2F7" p="8px" borderRadius="8px" mb="12px">
                <Stack spacing="12px">
                  <Flex align="flex-start" gap="4px" flexWrap="wrap">
                    <Text
                      fontSize="14px"
                      fontWeight="medium"
                      color="#000000"
                      flexShrink={0}
                    >
                      Reward
                    </Text>
                    <Text
                      color="#FF0000"
                      fontSize="14px"
                      fontWeight="medium"
                      flexShrink={0}
                    >
                      •
                    </Text>
                    <Text
                      color="#FF0000"
                      fontSize="14px"
                      fontWeight="medium"
                      wordBreak="break-word"
                    >
                      {reward.messages}
                    </Text>
                  </Flex>
                  <Stack spacing="4px">
                    {reward.products.map((product, productIdx) => (
                      <Text
                        key={productIdx}
                        fontSize="14px"
                        color="#000000"
                        textTransform="capitalize"
                      >
                        Pembelian {product.product_name}
                      </Text>
                    ))}
                  </Stack>
                </Stack>
              </Box>
            ))}
          </Flex>
        )}

        {/* Terms and Conditions Section */}
        {termMessages.length > 0 && (
          <Box>
            <Text
              fontSize={{ base: 'md', md: 'lg' }}
              fontWeight="bold"
              color="#000000"
              mb="12px"
            >
              Syarat dan ketentuan pemakaian voucher
            </Text>
            <UnorderedList spacing="8px">
              {termMessages.map((term, idx) => (
                <ListItem key={idx} fontSize="14px" color="#000000">
                  <Text
                    as="span"
                    dangerouslySetInnerHTML={{ __html: term.messages }}
                  />

                  {term.products.length > 0 && (
                    <UnorderedList mt="4px" ml="4" spacing="4px">
                      {term.products.map((product, productIdx) => (
                        <ListItem
                          key={productIdx}
                          fontSize="14px"
                          color="#000000"
                        >
                          <Text as="span" textTransform="capitalize">
                            {product.product_name}
                          </Text>
                        </ListItem>
                      ))}
                    </UnorderedList>
                  )}
                </ListItem>
              ))}
            </UnorderedList>
          </Box>
        )}

        {/* Warning Messages Section */}
        {warningMessages.length > 0 && (
          <Box>
            {warningMessages.map((warning, idx) => (
              <Flex key={idx} alignItems="start" justifyContent="start">
                <WarningAmberOutlined
                  sx={{
                    width: '16px',
                    height: '16px',
                    color: '#FF8D28',
                    marginRight: '8px',
                    marginTop: '2px',
                  }}
                />
                <Text fontSize="12px" color="#FF8D28" fontWeight="medium">
                  {warning.messages}
                </Text>
              </Flex>
            ))}
          </Box>
        )}
      </Flex>
    </Box>
  );
};

export default VoucherCardDetail;
